/**
 * Formatage humain d'un état d'alerte (`EtatAlerte` calculé côté back) pour
 * l'UI : phrase « dû dans X j » / « dépassé de X j » / « ~Y h ».
 *
 * Source de l'état : `evaluerAlerte` (domaine back) renvoie `joursRestants` et
 * `heuresRestantes` (négatif = dépassé, null = critère inactif). On ne refait
 * AUCUN calcul d'échéance ici — on se contente de mettre en français le verdict
 * du serveur (source de vérité). Partagé liste / fiche / alertes dashboard.
 */

/** Forme minimale de l'état d'alerte renvoyé par le back (sous-ensemble d'EtatAlerte). */
export interface EtatAlerteLike {
  estDue: boolean;
  joursRestants: number | null;
  heuresRestantes: number | null;
}

/** Formate un nombre d'heures restantes en « ~9 h » (1 décimale max, sans signe). */
function formatHeuresRestantes(heures: number): string {
  const abs = Math.abs(heures);
  return `${abs.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} h`;
}

/**
 * Volet calendaire en clair : « dû aujourd'hui » / « dû dans 9 j » / « dépassé
 * de 3 j ». Retourne `null` si le critère date est inactif.
 */
export function libelleEcheanceJours(joursRestants: number | null): string | null {
  if (joursRestants === null) return null;
  if (joursRestants < 0) {
    const j = Math.abs(joursRestants);
    return `dépassé de ${j} j`;
  }
  if (joursRestants === 0) return "dû aujourd'hui";
  return `dû dans ${joursRestants} j`;
}

/**
 * Volet horaire en clair : « dans ~9 h » / « dépassé de ~12 h ». `null` si le
 * critère horaire est inactif (matériel sans compteur ou plan sans heures).
 */
export function libelleEcheanceHeures(heuresRestantes: number | null): string | null {
  if (heuresRestantes === null) return null;
  if (heuresRestantes < 0) return `dépassé de ${formatHeuresRestantes(heuresRestantes)}`;
  return `dans ~${formatHeuresRestantes(heuresRestantes)}`;
}

/**
 * Résumé d'échéance combiné (date et/ou heures), pour une ligne de plan ou une
 * tuile. Joint les deux volets disponibles par « · ». Vide → « à jour ».
 */
export function libelleEcheanceCombine(etat: EtatAlerteLike): string {
  const parts = [
    libelleEcheanceJours(etat.joursRestants),
    libelleEcheanceHeures(etat.heuresRestantes),
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return "à jour";
  return parts.join(" · ");
}
